import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { getActiveUserDataRaw } from '../shared/utils/impersonation-handoff.util';

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

export interface RemoteLogEntry {
  dt: string;
  level: string;
  message: string;
  source: string;
  app: string;
  env: string;
  sessionId: string;
  userId?: string;
  userEmail?: string;
  userRole?: string;
  locationId?: string;
  url: string;
  data?: any;
}

/**
 * Centralized LoggerService for application-wide logging, local console output, and optional remote batching/flush to a logging endpoint.
 * @component
 * @selector app-logger-service
 * @example
 *   // Inject and use in a component or service
 *   constructor(private logger: LoggerService) {
 *     this.logger.info('Component initialized');
 *   }
 * @input {none} none - This injectable service has no Angular @Input properties. Configure behavior via environment.remoteLogging in environment files.
 * @output {none} none - This service does not expose Angular @Output events. It writes to the console and optionally sends batches to a remote endpoint.
 *
 * Purpose:
 *   - Provide a single, consistent API for logging at multiple levels (error, warn, info, debug).
 *   - Buffer logs locally and periodically flush them to a remote logging endpoint when remote logging is enabled.
 *   - Enrich log entries with session and user context, redact sensitive fields, and serialize errors for safe transport.
 *
 * Key public methods:
 *   - error(message: string, data?: any, source?: string): void
 *       Log an ERROR-level message. Triggers an immediate flush for error-level events.
 *   - warn(message: string, data?: any, source?: string): void
 *       Log a WARN-level message.
 *   - info(message: string, data?: any, source?: string): void
 *       Log an INFO-level message.
 *   - debug(message: string, data?: any, source?: string): void
 *       Log a DEBUG-level message.
 *
 * Workflow / domain helpers:
 *   - trackOrder(event: string, data?): void
 *       Helper to log order lifecycle events with structured metadata.
 *   - trackPayment(event: string, data?): void
 *       Helper to log payment lifecycle events including amount/status/context.
 *   - trackNotification(event: string, data?): void
 *       Helper to log notification lifecycle events and timing/queue data.
 *   - trackSse(eventName: string, channelName: string, data?: any): void
 *       Logs SSE events received from the realtime stream.
 *   - trackError(component: string, error: any, context?: any): void
 *       Captures and logs unhandled or caught errors with serialized error and context.
 *
 * Deprecated / legacy helpers:
 *   - logUserAction(action: string, data?: any): void (deprecated)
 *   - logApiCall(method: string, endpoint: string, status?: number, data?: any): void (deprecated; used by ApiService tap operators)
 *
 * Important private/core behaviors:
 *   - log(level, message, data?, source?): Filters messages by configured local logLevel and remoteMinLevel; writes to console in non-production or for warnings+; builds remote entries.
 *   - buildLogEntry(...): Assembles a RemoteLogEntry including timestamp, sessionId, URL, app/env and sanitized data/user context.
 *   - addToBuffer(entry, level): Keeps an in-memory ring buffer (MAX_BUFFER_SIZE) and triggers immediate flush on errors.
 *   - startFlushTimer(): When remote logging is enabled, starts a periodic timer (batchIntervalMs) to call flushNow.
 *   - flushNow(): Sends buffered logs to the remote endpoint if any and handles in-flight guard to prevent concurrent flushes.
 *   - sendBatch(entries, maxRetries?): Implements retry/backoff for transient server errors (429 / 5xx) and uses fetch with keepalive for navigation-safe delivery.
 *
 * Lifecycle hooks / page integration:
 *   - constructor(): Initializes session id, parses configured remote logging level and starts flush timer + page listeners if remote logging is enabled.
 *   - onBeforeUnload: Listener that attempts a final keepalive fetch of any remaining buffered logs when the page is unloading.
 *   - onVisibilityChange: Listener that triggers a flush when document.visibilityState becomes 'hidden'.
 *   - cleanup: The service relies on the page lifecycle and environment configuration; consumers do not need to manually call lifecycle methods.
 *
 * Utilities and safety:
 *   - getUserContext(): Extracts lightweight user context from localStorage safely.
 *   - sanitizeData(obj): Deep-clones and strips/serializes non-JSON-friendly values; returns primitive fallback when cloning fails.
 *   - redactSensitive(obj): Recursively replaces commonly sensitive keys (password, token, client_secret, card_number, cvv, authorization, etc.) with "[REDACTED]".
 *   - serializeError(error): Normalizes Error-like objects to a transportable shape (name, message, stack, status, statusText, url, error).
 *   - getOrCreateSessionId(): Generates and persists a session-scoped id in sessionStorage.
 *
 * Important configuration:
 *   - environment.enableConsoleLogs — when false, nothing is written to the browser console.
 *   - Reads environment.remoteLogging for { enabled, endpoint, token, minLevel, batchIntervalMs }.
 *   - MAX_BUFFER_SIZE defaults to 200 entries; on catastrophic send failures a recent subset (up to 50) is preserved to avoid total data loss.
 *
 * Notes:
 *   - This service is provided in root (singleton) and meant to be injected where logging/tracking is required.
 *   - Remote delivery uses fetch with keepalive to survive navigation and attempts exponential backoff on retryable failures.
 */
@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private readonly sessionId: string;
  private logBuffer: RemoteLogEntry[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private isFlushingInProgress = false;
  private readonly MAX_BUFFER_SIZE = 200;
  private readonly logLevel: LogLevel = LogLevel.DEBUG;
  private readonly remoteMinLevel: LogLevel;

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.remoteMinLevel = this.parseLevel(environment.remoteLogging?.minLevel ?? 'INFO');

    if (environment.remoteLogging?.enabled && environment.remoteLogging?.token) {
      this.startFlushTimer();
      window.addEventListener('beforeunload', this.onBeforeUnload);
      window.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  error(message: string, data?: any, source?: string): void {
    this.log(LogLevel.ERROR, message, data, source);
  }

  warn(message: string, data?: any, source?: string): void {
    this.log(LogLevel.WARN, message, data, source);
  }

  info(message: string, data?: any, source?: string): void {
    this.log(LogLevel.INFO, message, data, source);
  }

  debug(message: string, data?: any, source?: string): void {
    this.log(LogLevel.DEBUG, message, data, source);
  }

  // ─── Workflow Tracking Helpers ────────────────────────────────────────────────

  /**
   * Track an order lifecycle event (created, cancelled, viewed, etc.)
   */
  trackOrder(
    event: string,
    data?: {
      orderId?: string;
      customerId?: string;
      packageId?: string;
      packageName?: string;
      paymentMethod?: string;
      amount?: string | number;
      washType?: string;
      locationId?: string;
      source?: string;
      [key: string]: any;
    }
  ): void {
    this.info(`[Order] ${event}`, data, 'OrderFlow');
  }

  /**
   * Track a payment lifecycle event (initiated, succeeded, failed, link sent, etc.)
   */
  trackPayment(
    event: string,
    data?: {
      orderId?: string;
      paymentMethod?: string;
      paymentIntentId?: string;
      amount?: string | number;
      status?: string;
      error?: any;
      channel?: string;
      sseEvent?: string;
      [key: string]: any;
    }
  ): void {
    this.info(`[Payment] ${event}`, data, 'PaymentFlow');
  }

  /**
   * Track a notification lifecycle event (received, shown, dismissed, queued, etc.)
   */
  trackNotification(
    event: string,
    data?: {
      notificationType?: string;
      licensePlate?: string;
      locationId?: string;
      orderId?: string;
      queueLength?: number;
      timingMs?: number;
      [key: string]: any;
    }
  ): void {
    this.info(`[Notification] ${event}`, data, 'NotificationFlow');
  }

  /**
   * Track an SSE event received on the realtime stream
   */
  trackSse(eventName: string, channelName: string, data?: any): void {
    this.debug(`[SSE] ${channelName} → ${eventName}`, this.sanitizeData(data), 'SseEvents');
  }

  /** Per-event delivery log with connectionId (remote + console via caller). */
  trackSseEvent(
    eventName: string,
    data?: {
      connectionId?: string | null;
      event?: string;
      eventId?: string | null;
      mode?: string;
      at?: string;
      [key: string]: unknown;
    }
  ): void {
    this.info(`[SSE] event: ${eventName}`, data, 'SseEvents');
  }

  /** Connection lifecycle (connect / disconnect / reconnect) — remote + console. */
  trackSseConnection(event: string, data?: Record<string, any>): void {
    const phase = data?.['phase'];
    if (phase === 'connected' || phase === 'init') {
      this.info(`[SSE] ${event}`, data, 'SseConnection');
      return;
    }
    this.warn(`[SSE] ${event}`, data, 'SseConnection');
  }

  /**
   * Track an unhandled error with full context
   */
  trackError(component: string, error: any, context?: any): void {
    this.error(
      `[Error] ${component}`,
      {
        error: this.serializeError(error),
        context: this.sanitizeData(context),
      },
      component
    );
  }

  /** @deprecated Use trackOrder/trackPayment/trackNotification instead */
  logUserAction(action: string, data?: any): void {
    this.info(`User action: ${action}`, data, 'UserActions');
  }

  /** @deprecated Called automatically via ApiService tap operators */
  logApiCall(method: string, endpoint: string, status?: number, data?: any): void {
    const message = `API ${method} ${endpoint}${status ? ` (${status})` : ''}`;
    if (status && status >= 400) {
      this.error(message, data, 'API');
    } else {
      this.debug(message, data, 'API');
    }
  }

  logComponentLifecycle(component: string, lifecycle: string, data?: any): void {
    this.debug(`${component} - ${lifecycle}`, data, 'ComponentLifecycle');
  }

  // ─── Core Logging ────────────────────────────────────────────────────────────

  /**
   * Logs a message to the console and, when configured and appropriate by level/environment, enqueues it for remote logging.
   * @example
   * logger.log(LogLevel.ERROR, 'Failed to save item', { id: 123 }, 'SaveService')
   * undefined
   * @param {{LogLevel}} {{level}} - The severity level of the log entry.
   * @param {{string}} {{message}} - The message to log.
   * @param {{any}} {{data}} - Optional additional data to include with the log.
   * @param {{string}} {{source}} - Optional source identifier for the log (defaults to 'App').
   * @returns {{void}} Does not return a value.
   **/
  private log(level: LogLevel, message: string, data?: any, source?: string): void {
    if (level > this.logLevel) return;

    const timestamp = new Date().toISOString();

    if (environment.enableConsoleLogs) {
      this.logToConsole(level, timestamp, source ?? 'App', message, data);
    }

    if (
      environment.remoteLogging?.enabled &&
      environment.remoteLogging?.token &&
      level <= this.remoteMinLevel
    ) {
      const entry = this.buildLogEntry(level, message, source, data, timestamp);
      this.addToBuffer(entry, level);
    }
  }

  private logToConsole(
    level: LogLevel,
    timestamp: string,
    source: string,
    message: string,
    data?: any
  ): void {
    const prefix = `[${timestamp}] ${LogLevel[level]} [${source}]:`;
    switch (level) {
      case LogLevel.ERROR:
        console.error(prefix, message, data ?? '');
        break;
      case LogLevel.WARN:
        console.warn(prefix, message, data ?? '');
        break;
      case LogLevel.INFO:
        console.info(prefix, message, data ?? '');
        break;
      default:
        console.debug(prefix, message, data ?? '');
        break;
    }
  }

  /**
   * Builds a standardized remote log entry object from provided values and current runtime/user context.
   * @example
   * buildLogEntry(LogLevel.Info, 'User signed in', 'AuthService', { userId: 42 }, '2026-03-17T12:00:00Z')
   * { dt: '2026-03-17T12:00:00Z', level: 'info', message: 'User signed in', source: 'AuthService', app: 'car-wash-saas', env: 'production', sessionId: 'abc123', url: 'https://app.example.com/', userId: 42, data: { userId: 42 } }
   * @param {{LogLevel}} {{level}} - Log level enum value indicating severity or type.
   * @param {{string}} {{message}} - Human-readable log message.
   * @param {{string | undefined}} {{source}} - Originating source/name of the logger or component; defaults to 'App' if undefined.
   * @param {{any}} {{data}} - Additional payload or context to include with the log; will be sanitized before inclusion.
   * @param {{string}} {{timestamp}} - ISO timestamp string representing when the log was created.
   * @returns {{RemoteLogEntry}} Remote log entry object ready to be sent to the remote logging backend.
   */
  private buildLogEntry(
    level: LogLevel,
    message: string,
    source: string | undefined,
    data: any,
    timestamp: string
  ): RemoteLogEntry {
    const userCtx = this.getUserContext();
    return {
      dt: timestamp,
      level: LogLevel[level].toLowerCase(),
      message,
      source: source ?? 'App',
      app: 'car-wash-saas',
      env: environment.envName ?? (environment.production ? 'production' : 'local'),
      sessionId: this.sessionId,
      url: typeof window !== 'undefined' ? window.location.href : '',
      ...userCtx,
      data: this.sanitizeData(data),
    };
  }

  // ─── Buffer & Flushing ────────────────────────────────────────────────────────

  private addToBuffer(entry: RemoteLogEntry, level: LogLevel): void {
    this.logBuffer.push(entry);

    if (this.logBuffer.length > this.MAX_BUFFER_SIZE) {
      this.logBuffer = this.logBuffer.slice(-this.MAX_BUFFER_SIZE);
    }

    if (level === LogLevel.ERROR) {
      this.flushNow();
    }
  }

  private startFlushTimer(): void {
    const intervalMs = environment.remoteLogging?.batchIntervalMs ?? 5000;
    this.flushTimer = setInterval(() => this.flushNow(), intervalMs);
  }

  /**
   * Flush buffered logs to the remote logging service immediately if conditions allow.
   * @example
   * await logger.flushNow()
   * Promise<void>
   * @returns {Promise<void>} Resolves when the flush completes or returns early if flushing is already in progress, the buffer is empty, or remote logging is disabled / not configured.
   **/
  private async flushNow(): Promise<void> {
    if (this.isFlushingInProgress || this.logBuffer.length === 0) return;
    if (!environment.remoteLogging?.enabled || !environment.remoteLogging?.token) return;

    this.isFlushingInProgress = true;
    const batch = [...this.logBuffer];
    this.logBuffer = [];

    try {
      await this.sendBatch(batch);
    } catch {
      // On total failure, keep up to 50 most-recent entries to avoid losing everything
      this.logBuffer = [...batch.slice(-50), ...this.logBuffer].slice(-this.MAX_BUFFER_SIZE);
    } finally {
      this.isFlushingInProgress = false;
    }
  }

  /**
   * Sends a batch of log entries to the configured remote logging endpoint using a POST with exponential backoff retries.
   * @example
   * sendBatch([{ level: 'info', message: 'test' }], 2)
   * Promise<void>
   * @param {RemoteLogEntry[]} entries - Array of remote log entries to be uploaded in a single batch.
   * @param {number} [maxRetries=3] - Maximum number of retry attempts for transient/network errors.
   * @returns {Promise<void>} Resolves when the batch is sent or dropped; rejects if delivery fails after all retries.
   **/
  private async sendBatch(entries: RemoteLogEntry[], maxRetries = 3): Promise<void> {
    const { endpoint, token } = environment.remoteLogging!;
    const body = JSON.stringify(entries);

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body,
          keepalive: true, // Survives page navigation/close
        });

        if (response.ok) return;

        // Retryable server/rate-limit errors
        if (response.status === 429 || response.status >= 500) {
          if (attempt < maxRetries) {
            await this.delay(Math.pow(2, attempt) * 1000);
            continue;
          }
        }

        // Non-retryable (4xx except 429) — drop the batch
        return;
      } catch {
        if (attempt < maxRetries) {
          await this.delay(Math.pow(2, attempt) * 1000);
        } else {
          throw new Error('Log flush failed after all retries');
        }
      }
    }
  }

  // ─── Page Lifecycle Handlers ──────────────────────────────────────────────────

  private onBeforeUnload = (): void => {
    if (this.logBuffer.length === 0 || !environment.remoteLogging?.enabled) return;
    const { endpoint, token } = environment.remoteLogging!;
    try {
      // fetch with keepalive is the modern way — works with auth headers unlike sendBeacon
      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(this.logBuffer),
        keepalive: true,
      });
      this.logBuffer = [];
    } catch {
      /* silent — can't do much at page unload */
    }
  };

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.flushNow();
    }
  };

  // ─── Utilities ────────────────────────────────────────────────────────────────

  /**
   * Retrieve user context from localStorage ('userData') and return a Partial<RemoteLogEntry> with userId, userEmail, userRole, and locationId (returns {} if not in browser or on error).
   * @example
   * getUserContext()
   * { userId: '123', userEmail: 'alice@example.com', userRole: 'admin', locationId: 10 }
   * @returns {Partial<RemoteLogEntry>} Partial RemoteLogEntry containing any of userId, userEmail, userRole, locationId or an empty object.
   */
  private getUserContext(): Partial<RemoteLogEntry> {
    try {
      if (typeof window === 'undefined') return {};
      const raw = getActiveUserDataRaw();
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return {
        userId: parsed?.id ?? parsed?.user?.id ?? undefined,
        userEmail: parsed?.email ?? parsed?.user?.email ?? undefined,
        userRole: parsed?.role ?? parsed?.user?.role ?? undefined,
        locationId: parsed?.location_id ?? parsed?.user?.location_id ?? undefined,
      };
    } catch {
      return {};
    }
  }

  private sanitizeData(data: any): any {
    if (data === undefined || data === null) return undefined;
    try {
      const clone = JSON.parse(JSON.stringify(data));
      this.redactSensitive(clone);
      return clone;
    } catch {
      return typeof data === 'string' ? data : String(data);
    }
  }

  private redactSensitive(obj: any): void {
    if (!obj || typeof obj !== 'object') return;
    const SENSITIVE = [
      'password',
      'token',
      'client_secret',
      'card_number',
      'cvv',
      'cvc',
      'authorization',
      'stripe_key',
    ];
    for (const key of Object.keys(obj)) {
      if (SENSITIVE.some(s => key.toLowerCase().includes(s))) {
        obj[key] = '[REDACTED]';
      } else {
        this.redactSensitive(obj[key]);
      }
    }
  }

  /**
   * Serialize an Error-like value into a plain object with common error properties.
   * @example
   * serializeError(new Error('sample'))
   * { name: 'Error', message: 'sample', stack: 'Error: sample\n    at ...', status: undefined, statusText: undefined, url: undefined, error: undefined }
   * @param {{any}} {{error}} - The error value to serialize (may be an Error or HTTP error object).
   * @returns {{any}} Serialized error object containing name, message, stack, status, statusText, url and error.
   **/
  private serializeError(error: any): any {
    if (!error) return error;
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      error: error.error,
    };
  }

  private getOrCreateSessionId(): string {
    const KEY = 'app_session_id';
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      sessionStorage.setItem(KEY, id);
    }
    return id;
  }

  private parseLevel(name: string): LogLevel {
    return (LogLevel as any)[name.toUpperCase()] ?? LogLevel.INFO;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
